import { Component, HostListener, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ApiService } from '../../core/api/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { TokenService } from '../../core/auth/token.service';

interface MenuItem {
  icon: string;
  label: string;
  route?: string;
  roles: string[];
  children?: MenuItem[];
}

@Component({
  selector: 'fp-shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ReactiveFormsModule, NgTemplateOutlet],
  template: `
    <section class="shell" [class.sidebar-collapsed]="sidebarCollapsed">
      <aside class="sidebar" id="sidebar" (mouseenter)="sidebarCollapsed = false" (mouseleave)="closeMenu()">
        <div class="brand">
          <img class="brand-logo" src="/logo.png" alt="Fondos Proventus">
          <p>{{ role() }}</p>
        </div>

        <nav aria-label="Menú principal">
          @for (item of visibleMenu(); track item.label) {
            @if (visibleChildren(item).length) {
              <button
                class="menu-action menu-group"
                type="button"
                [title]="item.label"
                [class.active]="isMenuActive(item)"
                [attr.aria-expanded]="isGroupOpen(item)"
                [attr.aria-controls]="groupId(item)"
                (click)="toggleGroup(item)">
                <span class="menu-icon">
                  <i [class]="item.icon"></i>
                </span>
                <span class="menu-label">{{ item.label }}</span>
                <i class="bi bi-chevron-right menu-chevron" [class.open]="isGroupOpen(item)" aria-hidden="true"></i>
              </button>
              <div [id]="groupId(item)" class="submenu" [hidden]="!isGroupOpen(item)">
                <ng-container *ngTemplateOutlet="submenuItems; context: { $implicit: item }" />
              </div>
            } @else {
              <a [routerLink]="item.route" routerLinkActive="active" ariaCurrentWhenActive="page" [title]="item.label" (click)="closeMenu()">
                <span class="menu-icon">
                  <i [class]="item.icon"></i>
                </span>
                <span class="menu-label">{{ item.label }}</span>
              </a>
            }
          }
          <button class="menu-action" type="button" title="Cerrar sesión" (click)="logout()">
            <span class="menu-icon">
              <i class="bi bi-box-arrow-right"></i>
            </span>
            <span class="menu-label">Cerrar sesión</span>
          </button>
        </nav>
        <ng-template #submenuItems let-parent>
          @for (item of visibleChildren(parent); track item.label) {
            <div class="submenu-item">
              @if (visibleChildren(item).length) {
                <button class="submenu-link" type="button" [class.active]="isMenuActive(item)"
                  [attr.aria-expanded]="isGroupOpen(item)" [attr.aria-controls]="groupId(item)" (click)="toggleGroup(item)">
                  <span>{{ item.label }}</span>
                  <i class="bi bi-chevron-right menu-chevron" [class.open]="isGroupOpen(item)" aria-hidden="true"></i>
                </button>
                <div class="submenu" [id]="groupId(item)" [hidden]="!isGroupOpen(item)">
                  <ng-container *ngTemplateOutlet="submenuItems; context: { $implicit: item }" />
                </div>
              } @else {
                <a class="submenu-link" [routerLink]="item.route" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
                  {{ item.label }}
                </a>
              }
            </div>
          }
        </ng-template>
      </aside>

      @if (!sidebarCollapsed) {
        <button class="sidebar-backdrop" type="button" aria-label="Cerrar menú" (click)="toggleSidebar()"></button>
      }

      <main>
        <header class="topbar">
          <button class="menu-toggle" type="button" (click)="toggleSidebar()" [attr.aria-label]="sidebarCollapsed ? 'Abrir menú' : 'Contraer menú'" [attr.aria-expanded]="!sidebarCollapsed" aria-controls="sidebar">
            <i class="bi bi-list"></i>
          </button>

          <div class="session">
            <strong>{{ displayName() }}</strong>
            <span>{{ role() }}</span>
          </div>

          <button class="secondary" type="button" (click)="logout()">Salir</button>
        </header>

        <nav class="breadcrumb" aria-label="Ruta de navegación">
          @for (item of breadcrumb(); track item.label; let last = $last) {
            @if (!$first) { <span class="breadcrumb-separator" aria-hidden="true">/</span> }
            @if (item.route && !last) {
              <a [routerLink]="item.route">{{ item.label }}</a>
            } @else {
              <span [attr.aria-current]="last ? 'page' : null">{{ item.label }}</span>
            }
          }
        </nav>
        <section class="content">
          <router-outlet />
        </section>
      </main>

      @if (showPolicyModal) {
        <section class="modal-lock">
          <div class="modal-card">
            <i class="bi bi-shield-lock"></i>
            <h2>Politica de proteccion de datos</h2>
            <p>
              Para continuar debes aceptar el tratamiento de tus datos personales conforme a las politicas
              de Fondos Proventus. Esta aceptacion sera registrada para auditoria.
            </p>
            <button type="button" [disabled]="policyLoading" (click)="acceptPolicy()">
              {{ policyLoading ? 'Registrando...' : 'Acepto y continuar' }}
            </button>
            <button class="secondary" type="button" [disabled]="policyLoading" (click)="cancelPolicy()">
              Cancelar
            </button>
          </div>
        </section>
      }

      @if (!showPolicyModal && showPasswordModal) {
        <section class="modal-lock">
          <div class="modal-card">
            <i class="bi bi-key"></i>
            <h2>Cambio obligatorio de clave</h2>
            <p>Antes de usar el sistema debes cambiar tu clave temporal.</p>
            <form [formGroup]="passwordForm" (ngSubmit)="submitPasswordChange()">
              <label>Clave actual <input type="password" formControlName="passwordActual"></label>
              <label>Nueva clave <input type="password" formControlName="passwordNueva"></label>
              <label>Confirmar clave <input type="password" formControlName="confirmarPassword"></label>
              <button type="submit" [disabled]="passwordForm.invalid || passwordLoading">
                {{ passwordLoading ? 'Actualizando...' : 'Cambiar clave' }}
              </button>
              @if (modalMessage) {
                <p class="modal-error">{{ modalMessage }}</p>
              }
            </form>
          </div>
        </section>
      }
    </section>
  `,
  styleUrl: './shell.component.scss'
})
export class ShellComponent {
  private readonly authService = inject(AuthService);
  private readonly tokenService = inject(TokenService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  sidebarCollapsed = true;
  private readonly groupState = new Map<MenuItem, boolean>();
  clienteNombre = '';
  showPolicyModal = false;
  showPasswordModal = false;
  policyLoading = false;
  passwordLoading = false;
  modalMessage = '';

  passwordForm = this.fb.nonNullable.group({
    passwordActual: ['', Validators.required],
    passwordNueva: ['', [Validators.required, Validators.minLength(8)]],
    confirmarPassword: ['', [Validators.required, Validators.minLength(8)]]
  });

  readonly menu: MenuItem[] = [
    { icon: 'bi bi-speedometer2', label: 'Dashboard', route: '/dashboard', roles: ['ADMIN', 'OPERADOR', 'CLIENTE'] },
    {
      icon: 'bi bi-people-fill',
      label: 'Administración',
      roles: ['ADMIN'],
      children: [
        { icon: 'bi bi-people-fill', label: 'Usuarios', route: '/admin/usuarios', roles: ['ADMIN'] },
        { icon: 'bi bi-shield-check', label: 'Auditoria', route: '/admin/auditoria', roles: ['ADMIN'] }
      ]
    },
    {
      icon: 'bi bi-database-fill',
      label: 'Gestor',
      roles: ['ADMIN', 'OPERADOR'],
      children: [
        { icon: 'bi bi-person-check-fill', label: 'Accesos Gestor', route: '/admin/clientes-gestor', roles: ['ADMIN'] },
        { icon: 'bi bi-search-heart', label: 'Consulta clientes', route: '/operativo/clientes', roles: ['ADMIN', 'OPERADOR'] },
        { icon: 'bi bi-file-earmark-spreadsheet-fill', label: 'Carga bienes', route: '/operativo/carga-bienes-arrendados', roles: ['ADMIN', 'OPERADOR'] },
        { icon: 'bi bi-file-earmark-pdf-fill', label: 'Estados cuenta', route: '/operativo/estados-cuenta', roles: ['ADMIN', 'OPERADOR'] }
      ]
    },
    {
      icon: 'bi bi-clipboard-data-fill',
      label: 'Reportes',
      roles: ['ADMIN', 'OPERADOR'],
      children: [
        {
          icon: 'bi bi-building-fill',
          label: 'Bienes',
          roles: ['ADMIN', 'OPERADOR'],
          children: [
            { icon: 'bi bi-person-lines-fill', label: 'Bienes por cliente', route: '/reportes/bienes-gestor', roles: ['ADMIN', 'OPERADOR'] },
            { icon: 'bi bi-clipboard-data', label: 'Resumen arrendatarios', route: '/reportes/resumen-arrendatarios', roles: ['ADMIN', 'OPERADOR'] }
          ]
        },
        {
          icon: 'bi bi-person-check-fill',
          label: 'Personas',
          roles: ['ADMIN', 'OPERADOR'],
          children: [
            { icon: 'bi bi-search', label: 'Verificar persona', route: '/reportes/verificacion-persona', roles: ['ADMIN', 'OPERADOR'] }
          ]
        }
      ]
    },
    { icon: 'bi bi-person-vcard-fill', label: 'Perfil', route: '/cliente/perfil', roles: ['CLIENTE'] },
    { icon: 'bi bi-briefcase-fill', label: 'Productos', route: '/cliente/productos', roles: ['CLIENTE'] },
    { icon: 'bi bi-file-earmark-text-fill', label: 'Estado de cuenta', route: '/cliente/estado-cuenta', roles: ['CLIENTE'] },
    { icon: 'bi bi-arrow-left-right', label: 'Movimientos', route: '/cliente/movimientos', roles: ['CLIENTE'] },
    { icon: 'bi bi-key-fill', label: 'Cambiar clave', route: '/cambiar-password', roles: ['ADMIN', 'OPERADOR', 'CLIENTE'] }
  ];

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.groupState.clear();
      }
    });
    const isCliente = this.role() === 'CLIENTE';
    this.showPolicyModal = isCliente && this.tokenService.requiresPolicyAcceptance();
    this.showPasswordModal = isCliente && this.tokenService.requiresPasswordChange();

    if (this.role() === 'CLIENTE') {
      this.apiService.clientePerfil().subscribe({
        next: (profile) => {
          this.clienteNombre = profile.nombreCorrespondencia || profile.nombres || profile.username;
        }
      });
    }
  }

  username(): string {
    return this.tokenService.username() ?? '';
  }

  role(): string {
    return this.tokenService.role() ?? '';
  }

  displayName(): string {
    if (this.role() === 'CLIENTE' && this.clienteNombre) {
      return this.clienteNombre;
    }

    return this.username();
  }

  visibleMenu(): MenuItem[] {
    const role = this.role();
    return this.menu.filter((item) => item.roles.includes(role) && (!item.children || this.visibleChildren(item).length > 0));
  }

  visibleChildren(item: MenuItem): MenuItem[] {
    const role = this.role();
    return item.children?.filter((child) => child.roles.includes(role)) ?? [];
  }

  isMenuActive(item: MenuItem): boolean {
    if (item.route && this.router.isActive(item.route, {
      paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored'
    })) {
      return true;
    }

    return this.visibleChildren(item).some((child) => this.isMenuActive(child));
  }

  isGroupOpen(item: MenuItem): boolean {
    return this.groupState.get(item) ?? this.isMenuActive(item);
  }

  groupId(item: MenuItem): string {
    return 'submenu-' + item.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
  }

  toggleGroup(item: MenuItem): void {
    this.groupState.set(item, !this.isGroupOpen(item));
    if (this.sidebarCollapsed) {
      this.sidebarCollapsed = false;
    }
  }

  breadcrumb(): MenuItem[] {
    const findPath = (items: MenuItem[]): MenuItem[] => {
      for (const item of items) {
        if (this.isMenuActive(item)) {
          return [item, ...findPath(this.visibleChildren(item))];
        }
      }
      return [];
    };
    return findPath(this.visibleMenu());
  }

  closeMenu(): void {
    this.sidebarCollapsed = true;
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused.closest('#sidebar')) {
      focused.blur();
    }
  }

  @HostListener('document:keydown.escape')
  closeMenuWithEscape(): void {
    this.closeMenu();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  acceptPolicy(): void {
    this.policyLoading = true;
    this.authService.acceptDataPolicy().subscribe({
      next: () => {
        this.tokenService.markPolicyAccepted();
        this.showPolicyModal = false;
        this.showPasswordModal = this.tokenService.requiresPasswordChange();
        this.policyLoading = false;
      },
      error: () => {
        this.policyLoading = false;
        this.modalMessage = 'No se pudo registrar la aceptacion.';
      }
    });
  }

  cancelPolicy(): void {
    this.logout();
  }

  submitPasswordChange(): void {
    const payload = this.passwordForm.getRawValue();
    this.modalMessage = '';

    if (payload.passwordNueva !== payload.confirmarPassword) {
      this.modalMessage = 'La nueva clave y la confirmacion no coinciden.';
      return;
    }

    this.passwordLoading = true;
    this.authService.changePassword(payload).subscribe({
      next: () => {
        this.tokenService.markPasswordChanged();
        this.showPasswordModal = false;
        this.passwordLoading = false;
        this.passwordForm.reset();
      },
      error: (error) => {
        this.passwordLoading = false;
        this.modalMessage = error?.error?.message ?? 'No se pudo cambiar la clave. Revisa la clave actual.';
      }
    });
  }
}
